import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { trackClick } from '../utils/analytics';
import { DEEP_LINK_BASE_URL } from '../utils/common';
import {
  STORE_LINKS,
  detectPlatform,
  isExternalUrlSupported,
  openExternalUrl,
  isInsideFlutterApp,
} from '../utils/storeLinks';
import iconShare from '../assets/icon-share.svg';
import iconInstagram from '../assets/icon-instagram.svg';
import iconStar from '../assets/icon-star.svg';
import iconMail from '../assets/icon-mail.svg';

const INSTAGRAM_HANDLE = '@Koltora.app';
const INSTAGRAM_URL = 'https://www.instagram.com/koltora.app/';

type FlutterBridge = { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> };

interface MoreOptionsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const MoreOptionsSheet: React.FC<MoreOptionsSheetProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [isRateUsSupported, setIsRateUsSupported] = useState(false);
  const [isInstagramSupported, setIsInstagramSupported] = useState(false);

  // Check once on mount whether the native shell can open external links.
  // Older installed app builds predate the `openExternalUrl` handler, so on
  // those the Rate Us button (which needs to escape the WebView to the app
  // store) must stay hidden instead of silently failing when tapped.
  useEffect(() => {
    setIsRateUsSupported(isInsideFlutterApp() && isExternalUrlSupported());
    setIsInstagramSupported(isInsideFlutterApp() && isExternalUrlSupported());
  }, []);

  // Close on escape key + lock body scroll while open
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleShareApp = async (): Promise<void> => {
    trackClick('Share App', 'MoreOptions');
    const shareText = `${t('more_options_share_message')}\n${DEEP_LINK_BASE_URL}`;
    try {
      const flutterBridge = (window as Window & { flutter_inappwebview?: FlutterBridge })
        .flutter_inappwebview;
      if (flutterBridge) {
        await flutterBridge.callHandler('nativeShare', { text: shareText });
        return;
      }
      const shareData: ShareData = { text: shareText, url: DEEP_LINK_BASE_URL };
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else if (navigator.share) {
        await navigator.share({ text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        setShareFeedback(t('share_copied'));
        setTimeout(() => setShareFeedback(null), 2500);
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };

  const handleInstagram = (): void => {
    trackClick('Follow Instagram', 'MoreOptions');
    openExternalUrl(INSTAGRAM_URL).catch((err) => {
      console.error('openExternalUrl failed:', err);
    });
  };

  const handleRateUs = (): void => {
    trackClick('Rate Us', 'MoreOptions');
    const platform = detectPlatform();
    const storeUrl = platform === 'ios' ? STORE_LINKS.ios : STORE_LINKS.android;
    openExternalUrl(storeUrl).catch((err) => {
      console.error('openExternalUrl failed:', err);
    });
  };

  const handleContactUs = (): void => {
    trackClick('Contact Us', 'MoreOptions');
    onClose();
    navigate('/contact-us');
  };

  const options = [
    {
      id: 'share-app',
      icon: iconShare,
      title: t('more_options_share_app'),
      subtitle: shareFeedback ?? t('more_options_share_app_subtitle'),
      subtitleClass: 'text-black',
      onClick: () => void handleShareApp(),
      highlighted: false,
    },
    ...(isInstagramSupported
      ? [
          {
            id: 'instagram',
            icon: iconInstagram,
            title: t('more_options_instagram'),
            subtitle: INSTAGRAM_HANDLE,
            subtitleClass: 'text-[#4e02ff]',
            onClick: handleInstagram,
            highlighted: true,
          },
        ]
      : []),
    ...(isRateUsSupported
      ? [
          {
            id: 'rate-us',
            icon: iconStar,
            title: t('more_options_rate_us'),
            subtitle: t('more_options_rate_us_subtitle'),
            subtitleClass: 'text-black',
            onClick: handleRateUs,
            highlighted: false,
          },
        ]
      : []),
    {
      id: 'contact-us',
      icon: iconMail,
      title: t('menu_contact_us'),
      subtitle: t('more_options_contact_subtitle'),
      subtitleClass: 'text-black',
      onClick: handleContactUs,
      highlighted: false,
    },
  ];

  return (
    <>
      {/* Dimmed overlay */}
      {isOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 z-[1000] bg-black/50 transition-opacity duration-300"
          aria-hidden="true"
        />
      )}

      {/* Bottom sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 z-[1001] flex max-h-[85vh] flex-col rounded-t-[26px] bg-white transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={t('more_options_title') as string}
      >
        {/* Header */}
        <div className="relative flex items-center justify-center px-6 py-5">
          <h2 className="text-lg font-bold text-black">{t('more_options_title')}</h2>
          <button
            onClick={onClose}
            className="absolute end-5 flex h-8 w-8 items-center justify-center rounded-full text-lg font-medium text-gray-500"
            aria-label={t('menu_close') as string}
          >
            ✕
          </button>
        </div>

        {/* Options list */}
        <div className="flex flex-col gap-4 overflow-y-auto bg-[#e9edf4] p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={option.onClick}
              className={`flex w-full items-center gap-4 rounded-2xl bg-white px-5 py-5 text-start ${
                option.highlighted ? 'border-2 border-[#ac00f0]' : ''
              }`}
            >
              <img src={option.icon} alt="" className="h-12 w-12 shrink-0" />
              <span className="flex flex-col">
                <span className="text-[17px] font-bold text-black">{option.title}</span>
                <span className={`text-xs ${option.subtitleClass}`}>{option.subtitle}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default MoreOptionsSheet;
